import { useSequencingAccess } from '@/containers/sequences/use-sequencing-access.hook';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import { useExtensionNavigation } from '@/hooks/use-extension-navigation.hook';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useMemo } from 'react';

/**
 * Which nav entries show, and which carry the upgrade lock. One reading for
 * the sidebar and the ⌘K palette, so they cannot drift.
 */
export interface SidebarNavAccess {
  /** People and Companies: not part of the AppSumo plan. */
  showDirectory: boolean;
  /** The agents surface (`useAgentsAccess`), for the agents' own entries. */
  agentsUnlocked: boolean;
  /** Sequences, Tasks, Inbox and sequencing settings. */
  sequencingEnabled: boolean;
  /**
   * Sequencing is rolled out but not on the plan (Scale), as for every
   * AppSumo-only org: Sequences stays as a locked entry that opens the
   * upgrade page, as do Tasks and Inbox; sequencing settings leave the nav.
   */
  sequencingLocked: boolean;
  integrationsLocked: boolean;
}

export function useSidebarNavAccess(): SidebarNavAccess {
  const { hasFeature, isReady, error, isAppSumoOnly, Feature, actions } =
    useFeatureAccess();
  const { unlocked: agentsUnlocked } = useAgentsAccess();
  const { enabled: sequencingEnabled } = useSequencingAccess();
  const { showIntegrationsSurface } = useExtensionNavigation();
  // AppSumo-only orgs get no directory entries at all rather than locked ones.
  // Fail open: a plans or feature-access error leaves the entries visible and
  // the route-level AppSumoGate makes the call.
  const resolved = isReady && !error;
  const showDirectory = !(resolved && isAppSumoOnly);
  const integrationsLocked =
    !showIntegrationsSurface &&
    resolved &&
    !hasFeature(Feature.CRM_INTEGRATIONS);
  const sequencingLocked =
    sequencingEnabled &&
    resolved &&
    !(actions?.sequences_build ?? hasFeature(Feature.ENGAGE));
  return useMemo(
    () => ({
      showDirectory,
      agentsUnlocked,
      sequencingEnabled,
      sequencingLocked,
      integrationsLocked,
    }),
    [
      showDirectory,
      agentsUnlocked,
      sequencingEnabled,
      sequencingLocked,
      integrationsLocked,
    ]
  );
}
